import * as microsoftTeams from "@microsoft/teams-js";
import CustomerConfig from "../azure/CustomerConfig";
import { logger } from "../utils/logger";

/** Delay (ms) between opening successive auth dialogs so the previous one is fully closed. */
const DELAY_BETWEEN_DIALOGS_MS = 500;
/** Delay (ms) before retrying when embedded browser fails to open URL. */
const RETRY_AFTER_EMBEDDED_FAIL_MS = 1200;

/** Auth window/popup dimensions (width x height). */
const AUTH_WINDOW_WIDTH = 800;
const AUTH_WINDOW_HEIGHT = 650;

/** Error string from Teams when the dialog URL cannot be opened in the embedded browser (e.g. on web). */
const EMBEDDED_BROWSER_UNABLE_TO_OPEN = "URL was unable to be opened in embedded browser";

/** postMessage type from auth-callback.html when opened in a popup. */
const AUTH_CALLBACK_SUCCESS = "auth-callback-success";
const AUTH_CALLBACK_ERROR = "auth-callback-error";

/** localStorage key: when set, startLogin() returns without opening the auth dialog (used after logout until user clicks Retry). */
export const SKIP_AUTH_DIALOG_AFTER_LOGOUT = "skipAuthDialogAfterLogout";
/** localStorage key: when set, startLogin() returns without opening auth dialog after user canceled. */
export const SKIP_AUTH_DIALOG_AFTER_CANCEL = "skipAuthDialogAfterCancel";

class AuthService {
    constructor() {
        this.isAuthenticated = false;
        this.tokens = null;
        this.preopenedPopup = null;
    }

    /**
     * IFS login uses 3 dialog attempts in order:
     * 1. prompt=none  – Silent SSO; no UI if user already has IFS session. Often fails with login_required.
     * 2. prompt=select_account – Account picker. May fail on web with "URL was unable to be opened in embedded browser".
     * 3. prompt=login – Full login UI. Same proxy URL pattern; last chance for user to sign in.
     * Each dialog opens ifs-auth-proxy.html?ifsUrl=<encoded IFS OAuth URL>; the proxy redirects to IFS, then IFS redirects to auth-callback.html with ?code=...
     * @param {{ silentOnly?: boolean }} [options] - When silentOnly is true (automatic session refresh), only prompt=none
     *   is attempted — no popups, no select_account/login. User-initiated login must use silentOnly false (default).
     */
    async startLogin(options = {}) {
        const silentOnly = options.silentOnly === true;
        if (localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT) || localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_CANCEL)) {
            logger.auth("Skipping auth dialog auto-open (user must click Retry to open)", null);
            return {
                status: 401,
                success: false,
                data: null,
                message: "Authentication deferred until user clicks Retry",
            };
        }
        if (silentOnly) {
            return await this.startLoginSilentOnly();
        }
        let lastWasEmbeddedBrowserFail = false;
        try {
            // Pre-open popup as early as possible (user-gesture timing) to avoid browser popup blockers.
            this.preOpenPopup();

            // In browser web mode, prefer popup flow first; Teams embedded auth often fails to open URL.
            if (this.isLikelyWebClient()) {
                logger.auth("Web client detected; using popup-first authentication flow", null);
                const popupSelectAccount = await this.authenticateWithPopup("select_account");
                if (popupSelectAccount?.code) {
                    logger.auth("Received authorization code via popup", { codeLength: popupSelectAccount.code?.length });
                    localStorage.setItem("ifsUserCode", popupSelectAccount.code);
                    return { status: 200, success: true, data: popupSelectAccount.code, message: "Authentication successful" };
                }
                if (popupSelectAccount?.cancelledByUser) {
                    try { localStorage.setItem(SKIP_AUTH_DIALOG_AFTER_CANCEL, "1"); } catch (_) {}
                    return {
                        status: 401,
                        success: false,
                        data: null,
                        error: "Authentication failed: CancelledByUser",
                        message: "Authentication deferred until user clicks Retry",
                    };
                }

                const popupLogin = await this.authenticateWithPopup("login");
                if (popupLogin?.code) {
                    logger.auth("Received authorization code via popup", { codeLength: popupLogin.code?.length });
                    localStorage.setItem("ifsUserCode", popupLogin.code);
                    return { status: 200, success: true, data: popupLogin.code, message: "Authentication successful" };
                }
                if (popupSelectAccount?.cancelledByUser || popupLogin?.cancelledByUser) {
                    try { localStorage.setItem(SKIP_AUTH_DIALOG_AFTER_CANCEL, "1"); } catch (_) {}
                    return {
                        status: 401,
                        success: false,
                        data: null,
                        error: "Authentication failed: CancelledByUser",
                        message: "Authentication deferred until user clicks Retry",
                    };
                }
            }

            const steps = [
                { prompt: 'none', label: 'prompt=none' },
                { prompt: 'select_account', label: 'prompt=select_account' },
                { prompt: 'login', label: 'prompt=login' },
            ];

            for (let i = 0; i < steps.length; i++) {
                if (i > 0) {
                    await this.delay(DELAY_BETWEEN_DIALOGS_MS);
                }
                const { prompt, label } = steps[i];
                const result = await this.tryDialogWithRetry(prompt, label, (embeddedFail) => { if (embeddedFail) lastWasEmbeddedBrowserFail = true; });
                if (result.success && result.code) {
                    logger.auth("Received authorization code from callback", { codeLength: result.code?.length });
                    localStorage.setItem("ifsUserCode", result.code);
                    return { status: 200, success: true, data: result.code, message: 'Authentication successful' };
                }
                if (result.cancelledByUser) {
                    // prompt=none is silent/non-interactive; some clients report CancelledByUser here.
                    // Do not stop auth flow on this step.
                    if (prompt === "none") {
                        logger.warn("Ignoring CancelledByUser on prompt=none; continuing to interactive auth", null, "AUTH");
                        continue;
                    }
                    try { localStorage.setItem(SKIP_AUTH_DIALOG_AFTER_CANCEL, "1"); } catch (_) {}
                    return { status: 401, success: false, error: result.error, message: result.error };
                }
            }

            if (lastWasEmbeddedBrowserFail) {
                logger.auth("Embedded browser failed (web mode); trying popup fallback", null);
                const popupResult = await this.authenticateWithPopup("login");
                if (popupResult?.code) {
                    logger.auth("Received authorization code via popup", { codeLength: popupResult.code?.length });
                    localStorage.setItem("ifsUserCode", popupResult.code);
                    return { status: 200, success: true, data: popupResult.code, message: 'Authentication successful' };
                }
            }

            return {
                status: 401,
                success: false,
                data: null,
                message: 'Authentication process completed without a code'
            };
        } catch (error) {
            logger.error("Login initialization failed", error, "AUTH");
            const statusCode = error.statusCode ?? (error.message?.includes('Teams authentication API not available') ? 503
                : error.message?.includes('Authentication failed') ? 401
                : error.message?.includes('network') || error.message?.includes('fetch') ? 503
                : 500);
            return {
                status: statusCode,
                success: false,
                error: error.message,
                message: 'Authentication failed'
            };
        }
    }

    /**
     * Internal refresh only: hidden iframe + prompt=none (state auth_silent_*).
     * Does not call microsoftTeams.authentication.authenticate — that always opens a host dialog on desktop.
     * If silent SSO fails, returns 401 so the user can sign in from the login page / Retry.
     */
    async startLoginSilentOnly() {
        logger.auth("IFS silent refresh: hidden iframe prompt=none only (no Teams auth dialog)", null);

        const onCode = (code) => {
            localStorage.setItem("ifsUserCode", code);
            return {
                status: 200,
                success: true,
                data: code,
                message: "Authentication successful",
            };
        };

        const iframeResult = await this.authenticateWithHiddenIframe("none");
        if (iframeResult?.code) {
            return onCode(iframeResult.code);
        }

        return {
            status: 401,
            success: false,
            data: null,
            message: "Silent IFS refresh failed — session may require sign-in from the login page",
        };
    }

    /**
     * Hidden iframe OAuth with prompt=none. auth-callback posts to parent via state prefix auth_silent_.
     * @param {'none'} promptType
     * @returns {Promise<{ code: string } | null>}
     */
    authenticateWithHiddenIframe(promptType = "none") {
        const ifsUrl = this.generateIFSURL(promptType, "silent");
        const proxyUrl = `${window.location.origin}/ifs-auth-proxy.html?ifsUrl=${encodeURIComponent(ifsUrl)}`;
        const origin = window.location.origin;
        const TIMEOUT_MS = 120000;

        return new Promise((resolve) => {
            let settled = false;
            let timeoutId;
            const iframe = document.createElement("iframe");
            iframe.style.cssText = "position:fixed;width:0;height:0;border:0;visibility:hidden";
            iframe.setAttribute("aria-hidden", "true");
            iframe.title = "IFS silent auth";

            const cleanup = () => {
                window.removeEventListener("message", onMessage);
                if (iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                }
            };

            const finish = (value) => {
                if (settled) return;
                settled = true;
                if (timeoutId != null) {
                    clearTimeout(timeoutId);
                }
                cleanup();
                resolve(value);
            };

            const onMessage = (event) => {
                if (event.origin !== origin) return;
                const { type, payload } = event.data || {};
                if (type === AUTH_CALLBACK_SUCCESS && payload?.code) {
                    finish({ code: payload.code });
                } else if (type === AUTH_CALLBACK_ERROR) {
                    finish(null);
                }
            };

            window.addEventListener("message", onMessage);
            timeoutId = setTimeout(() => finish(null), TIMEOUT_MS);
            document.body.appendChild(iframe);
            iframe.src = proxyUrl;
        });
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    isLikelyWebClient() {
        const ua = navigator.userAgent || "";
        // Desktop Teams usually contains "Teams/".
        return !ua.includes("Teams/");
    }

    getCenteredPopupFeatures() {
        const screenLeft = window.screenLeft ?? window.screenX ?? 0;
        const screenTop = window.screenTop ?? window.screenY ?? 0;
        const viewportWidth = window.outerWidth ?? window.innerWidth ?? AUTH_WINDOW_WIDTH;
        const viewportHeight = window.outerHeight ?? window.innerHeight ?? AUTH_WINDOW_HEIGHT;
        const left = Math.max(0, Math.round(screenLeft + (viewportWidth - AUTH_WINDOW_WIDTH) / 2));
        const top = Math.max(0, Math.round(screenTop + (viewportHeight - AUTH_WINDOW_HEIGHT) / 2));

        return `width=${AUTH_WINDOW_WIDTH},height=${AUTH_WINDOW_HEIGHT},left=${left},top=${top},scrollbars=no,resizable=yes`;
    }

    preOpenPopup() {
        if (this.preopenedPopup && !this.preopenedPopup.closed) return;
        this.preopenedPopup = window.open("", "ifsAuth", this.getCenteredPopupFeatures());
        if (this.preopenedPopup) {
            logger.auth("Pre-opened auth popup to avoid popup blocker", null);
        }
    }

    /**
     * Try one auth dialog (e.g. prompt=none). On "URL was unable to be opened in embedded browser", retry once after a delay.
     * @param {string} promptType - 'none' | 'select_account' | 'login'
     * @param {string} label - For logging
     * @param {(embeddedFail: boolean) => void} [onEmbeddedFail] - Called with true when failure is embedded-browser
     * @returns {{ success: boolean, code?: string, cancelledByUser?: boolean, error?: string }}
     */
    async tryDialogWithRetry(promptType, label, onEmbeddedFail) {
        const tryOnce = async () => {
            const ifsUrl = this.generateIFSURL(promptType, "teams");
            const proxyUrl = `${window.location.origin}/ifs-auth-proxy.html?ifsUrl=${encodeURIComponent(ifsUrl)}`;
            logger.auth(`Starting authentication via proxy (${label})`, { ifsUrl, proxyUrl });

            try {
                const result = await this.authenticateWithTeams(proxyUrl);
                if (result && result.code) {
                    if (onEmbeddedFail) onEmbeddedFail(false);
                    return { success: true, code: result.code };
                }
                return { success: false };
            } catch (error) {
                const msg = error.message || '';
                const isEmbeddedFail = msg.includes(EMBEDDED_BROWSER_UNABLE_TO_OPEN);
                if (onEmbeddedFail) onEmbeddedFail(isEmbeddedFail);
                logger.warn(`Dialog with ${label} failed`, { message: msg }, "AUTH");
                if (msg === "Authentication failed: CancelledByUser") {
                    return { success: false, cancelledByUser: true, error: msg };
                }
                if (isEmbeddedFail) {
                    logger.auth(`Embedded browser could not open URL; will retry once after ${RETRY_AFTER_EMBEDDED_FAIL_MS}ms`, null);
                    await this.delay(RETRY_AFTER_EMBEDDED_FAIL_MS);
                    try {
                        const retryResult = await this.authenticateWithTeams(proxyUrl);
                        if (retryResult && retryResult.code) {
                            return { success: true, code: retryResult.code };
                        }
                    } catch (retryErr) {
                        logger.warn(`Dialog retry with ${label} failed`, { message: retryErr.message }, "AUTH");
                    }
                }
                return { success: false };
            }
        };

        return await tryOnce();
    }

    /**
     * Web fallback: open IFS auth in a popup and get the code via postMessage from auth-callback.html.
     * Used when Teams embedded browser fails (e.g. Teams web client).
     * @returns {Promise<{ code: string } | null>}
     */
    authenticateWithPopup(promptType = "login") {
        const ifsUrl = this.generateIFSURL(promptType, "popup");
        const proxyUrl = `${window.location.origin}/ifs-auth-proxy.html?ifsUrl=${encodeURIComponent(ifsUrl)}`;
        const origin = window.location.origin;

        return new Promise((resolve) => {
            let popup = this.preopenedPopup;
            if (popup && !popup.closed) {
                popup.location.href = proxyUrl;
                logger.auth(`Using pre-opened popup for auth (${promptType})`, { proxyUrl });
            } else {
                popup = window.open(proxyUrl, "ifsAuth", this.getCenteredPopupFeatures());
            }
            if (!popup) {
                logger.warn("Popup blocked; allow popups for this site to sign in", null, "AUTH");
                resolve(null);
                return;
            }

            const handleMessage = (event) => {
                if (event.origin !== origin) return;
                const { type, payload } = event.data || {};
                if (type === AUTH_CALLBACK_SUCCESS && payload?.code) {
                    window.removeEventListener("message", handleMessage);
                    clearInterval(interval);
                    this.preopenedPopup = null;
                    resolve({ code: payload.code });
                } else if (type === AUTH_CALLBACK_ERROR) {
                    window.removeEventListener("message", handleMessage);
                    clearInterval(interval);
                    logger.warn("Popup auth failed", { payload }, "AUTH");
                    this.preopenedPopup = null;
                    resolve(null);
                }
            };

            const interval = setInterval(() => {
                if (popup.closed) {
                    clearInterval(interval);
                    window.removeEventListener("message", handleMessage);
                    this.preopenedPopup = null;
                    resolve({ cancelledByUser: true });
                }
            }, 300);

            window.addEventListener("message", handleMessage);
        });
    }

    /** @param {'teams' | 'popup' | 'silent'} authMode - silent uses state auth_silent_* so callback postMessages parent (iframe). */
    generateIFSURL(promptType = "select_account", authMode = "teams") {
        const clientId = new CustomerConfig().getIfsClientId();
        // Keep redirect URI exact to the registered callback URL.
        // Some IdPs reject additional query params with "Invalid parameter: redirect_uri".
        const redirectUri = encodeURIComponent(`${window.location.origin}/auth-callback.html`);
        const scope = 'openid';
        const responseType = 'code';        

        // Generate random state
        const state = `auth_${authMode}_${promptType}_${Math.random().toString(36).slice(2, 11)}`;

        // Build Parameters
        const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            scope: scope,
            response_type: responseType,
            prompt: promptType,
            state
        }).toString();

        // Build IFS URL
        const ifsUrl = `${new CustomerConfig().getIfsTokenEndPoint()}?${params}`;
        return ifsUrl;
    }

    generateRandomString(length) {
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        return btoa(String.fromCharCode(...array))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '')
            .substring(0, length);
    }

    async authenticateWithTeams(authUrl) {
        return new Promise((resolve, reject) => {
            if (microsoftTeams.authentication && microsoftTeams.authentication.authenticate) {
                logger.auth("Using Teams authentication with proxy", { authUrl });

                microsoftTeams.authentication.authenticate({
                    url: authUrl,
                    width: AUTH_WINDOW_WIDTH,
                    height: AUTH_WINDOW_HEIGHT,
                    successCallback: (result) => {
                        logger.auth("Teams authentication successful", result);
                        const parsed =
                            typeof result === "string"
                                ? (() => {
                                      try {
                                          return JSON.parse(result);
                                      } catch {
                                          return { code: result };
                                      }
                                  })()
                                : result;
                        resolve(parsed);
                    },
                    failureCallback: (reason) => {
                        logger.error("Teams authentication failed", reason, "AUTH");
                        // Convert to a more descriptive error
                        const errorMsg = typeof reason === 'string' ? reason : 'Authentication was canceled or failed';
                        reject(new Error(`Authentication failed: ${errorMsg}`));
                    }
                });
            } else {
                reject(new Error('Teams authentication API not available'));
            }
        });
    }

}

const authService = new AuthService();
export { authService };